import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tracks whether an `<img>` failed to load, including failures that happen
 * before React hydrates (server-rendered images fire `error` before React
 * attaches `onError`, so the element is checked once after mount).
 *
 * @example
 * const { ref, failed, onError } = useImageLoadFailed(url);
 * return failed ? <Initials /> : <img ref={ref} src={url} onError={onError} />;
 */
export function useImageLoadFailed(url?: string | null) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const onError = useCallback(() => {
    if (url) setFailedUrl(url);
  }, [url]);

  useEffect(() => {
    const image = ref.current;

    // Already finished loading with no pixels: it failed before hydration.
    if (url && image && image.complete && image.naturalWidth === 0) {
      setFailedUrl(url);
    }
  }, [url]);

  return { ref, failed: Boolean(url) && failedUrl === url, onError };
}
